import { useState } from "react";
import { getMetricTooltip } from './metric_tooltips';

export function LeaderboardTable({ data, task }) {
	const [detailView, setDetailView] = useState({ isOpen: false, model: null, metric: null });

	if (!data || data.length === 0) {
		return (
			<div className="w-full border-1 border-gray-700 rounded-lg bg-gray-900/20 shadow-lg backdrop-blur-sm p-8 text-center">
				<p className="text-gray-400">No data available</p>
			</div>
		);
	}

	const allMetricKeys = Object.keys(data[0]).filter(
		key => !['rank', 'modelName', 'modelType', 'params', 'contextLength', 'url', 'version', 'architecture', 'Aggregated Score'].includes(key)
	);

	const getMetricsForTask = (task, availableKeys) => {
		if (task === "Rule Completion") {
			return ['Syntax', 'Functionality'];
		}
		if (task === "Module Completion") {
			const desiredMetrics = ['Syntax (STX)', 'Functionality (EQV)', 'Cov Mean'];
			return desiredMetrics.filter(metric => availableKeys.includes(metric));
		}
		return availableKeys;
	};

	const metricKeys = getMetricsForTask(task, allMetricKeys);

	const cleanMetricName = (name) => {
		if (name === "Cov Mean") {
			return "Cell Coverage";
		} else if (name === "EM") {
			return "Exact Matching"
		}
		if (task === "Module Completion") {
			if (name.includes("(EQV)")) {
				return name;
			}
			return name.replace(/\s*\([^)]*\)/g, '').trim();
		}
		return name.replace(/\s*\([^)]*\)/g, '').trim();
	};

	return (
		<div className="w-full border-1 border-gray-700 rounded-lg bg-gray-900/20 shadow-lg backdrop-blur-sm overflow-hidden">
			<div
				className="overflow-x-auto min-h-[536px] max-h-[536px] overflow-y-auto"
				style={{
					scrollbarWidth: 'thin',
					scrollbarColor: '#4B5563 #1F2937'
				}}
			>
				<style>{`
					div::-webkit-scrollbar {
						width: 8px;
						height: 8px;
					}
					div::-webkit-scrollbar-track {
						background: #1F2937;
						border-radius: 4px;
					}
					div::-webkit-scrollbar-thumb {
						background: #4B5563;
						border-radius: 4px;
					}
					div::-webkit-scrollbar-thumb:hover {
						background: #6B7280;
					}
				`}</style>
				<table className="w-full">
					<thead className="sticky top-0 z-10 border-b border-gray-700" style={{ backgroundColor: '#1A212B' }}>
						<tr>
							<th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
								Rank
							</th>
							<th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
								Model Type
							</th>
								<th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
									Model Name
								</th>
								{task === "Rule Completion" && (
									<th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
										Prompt Mode
									</th>
								)}
								<th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
									Params
								</th>
							{task === "Module Completion" && (
								<th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
									Context Len
								</th>
							)}
							{metricKeys.map((key, index) => {
								const tooltipData = getMetricTooltip(key, task);
								const isLastColumn = index === metricKeys.length - 1;

								return (
									<th key={key} className={`py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider ${index === 0 ? 'border-l border-gray-600 pl-6 pr-2' : 'px-2'}`}>
										<div className="flex items-center justify-center gap-1">
											<span>{cleanMetricName(key)}</span>
											<div className="relative group inline-block">
												<span className="cursor-help text-gray-500 hover:text-gray-300 transition-colors text-base">ⓘ</span>
												<div className="absolute right-0 top-full mt-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 z-50 w-64">
													<div className="bg-gray-800/95 backdrop-blur-sm text-gray-200 text-sm normal-case font-normal rounded-lg shadow-xl border border-gray-700 p-3">
														{tooltipData.content}
													</div>
												</div>
											</div>
										</div>
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody className="">
						{data.map((row, index) => (
							<tr key={index} className="border-b border-gray-700 hover:bg-gray-800/30 transition-colors">
								<td className="px-4 py-3 text-sm text-gray-300 text-center">
									{row.rank}
								</td>
								<td className="px-4 py-3 text-sm text-left">
									<span className={`px-2 py-1 rounded text-xs font-medium ${getModelTypeBadgeColor(row.modelType)}`}>
										{row.modelType}
									</span>
								</td>
								<td className="px-4 py-3 text-sm text-gray-200 font-medium text-left">
									<div className="flex items-center gap-2">
										<a href={row.url} target="_blank" rel="noopener noreferrer" className="hover:text-brand transition-colors">
											{row.modelName}
										</a>
										{row.version === "V6" && (
											<span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-green-500/15 border border-green-500/30 text-green-300">
											new
											</span>
										)}
									</div>
								</td>
								{task === "Rule Completion" && (
									<td className="px-4 py-3 text-sm text-gray-300 text-center">
										{row.promptMode || '*'}
									</td>
								)}
								<td className="px-4 py-3 text-sm text-gray-300 text-center">
								{row.params ? `${row.params}B` : 'N/A'}
								</td>
								{task === "Module Completion" && (
									<td className="px-4 py-3 text-sm text-gray-300 text-center">
										{row.contextLength || 'N/A'}
									</td>
								)}
								{metricKeys.map((key, index) => (
									<td 
										key={key} 
										className={`py-3 text-sm text-gray-300 text-center ${index === 0 ? 'border-l border-gray-700 pl-6 pr-2' : 'px-2'} ${
											task === "Rule Completion" && (key === "Syntax" || key === "Functionality") 
											? "cursor-pointer hover:text-white hover:underline" 
											: ""
										}`}
										onClick={() => {
											if (task === "Rule Completion" && (key === "Syntax" || key === "Functionality")) {
												setDetailView({ isOpen: true, model: row, metric: key });
											}
										}}
									>
										{typeof row[key] === 'number' ? row[key].toFixed(2) : row[key]}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{detailView.isOpen && (
				<div 
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
					onClick={() => setDetailView({ isOpen: false, model: null, metric: null })}
				>
					<div 
						className="bg-gray-900 border border-gray-700 rounded-lg shadow-2xl max-w-4xl w-full overflow-hidden"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50 flex justify-between items-center">
							<h3 className="text-lg font-semibold text-gray-200">
								Detailed {detailView.metric} Scores: {detailView.model?.modelName} ({detailView.model?.promptMode})
							</h3>
							<button 
								onClick={() => setDetailView({ isOpen: false, model: null, metric: null })}
								className="text-gray-400 hover:text-white transition-colors"
							>
								✕
							</button>
						</div>
						<div className="p-6 overflow-x-auto">
							<table className="w-full text-center border-collapse">
								<thead>
									<tr className="border-b border-gray-700">
										<th className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">Rule Type</th>
										<th className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">Score</th>
									</tr>
								</thead>
								<tbody>
									{["INST", "CONT", "BLK", "NBLK", "CASE", "COND", "ALWS", "PARAM", "PORT"].map(rule => {
										const metricSuffix = detailView.metric === "Syntax" ? " (STX)" : " (EQV)";
										const value = detailView.model?.[`${rule}${metricSuffix}`];
										return (
											<tr key={rule} className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors">
												<td className="px-4 py-2 text-sm text-gray-300">{rule}</td>
												<td className="px-4 py-2 text-sm text-gray-200 font-medium">
													{typeof value === 'number' ? `${value.toFixed(2)}%` : `${value}%`}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

function getModelTypeBadgeColor(type) {
	switch (type) {
		case 'General':
			return 'bg-yellow-500/20 text-yellow-200';
		case 'Coding':
			return 'bg-blue-500/20 text-blue-200';
		case 'RTL-Specific':
			return 'bg-green-500/20 text-green-200';
		default:
			return 'bg-gray-500/20 text-gray-200';
	}
}
